import { serializeAgendaToTopics } from './serialization'
import { AgendaEditorForm, AgendaProject, Participant, ParticipantsFormValues } from '../agendaTypes'

type ApiTopic = {
    topicId?: number | null
    parentTopicId: number | null
    topicName: string
    topicDescription?: string
    projects?: AgendaProject[]
    projectDescription?: string
    topicSummary?: string
    children: ApiTopic[]
}

type ApiAgenda = {
    agendaId: number
    agendaNo: string
    mailId?: string
    mailID?: string
    agendaName: string
    meetingUrl: string
    meetingDate: string
    meetingStartTime: string
    meetingEndTime: string
    meetingLocation: string
    agendaDescription: string
    createdAt: string
    submittedStatusId: number
    isDraft: boolean
    agendaSummary: string
    participants: {
        attendances?: Participant[]
        onLeaves?: Participant[]
        externalAttendances?: Participant[]
        secretaries?: Participant[]
        presidents?: Participant[]
        vicePresidents?: Participant[]
    }
    topics: ApiTopic[]
    remark: string
}

export const fmtThaiDateRange = (dayIso: string, startIso?: string, endIso?: string) => {
    if (!dayIso) return ''
    const tz = 'Asia/Bangkok'

    const d = new Date(dayIso)
    const parts = new Intl.DateTimeFormat('th-TH-u-ca-buddhist', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: tz,
    }).formatToParts(d)
    const pick = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? ''
    const datePart = `${pick('weekday')}ที่ ${pick('day')} ${pick('month')} ${pick('year')}`

    const t = (iso?: string) => {
        if (!iso) return ''
        const dt = new Date(iso)
        const hh = String(dt.getUTCHours()).padStart(2, '0')
        const mm = String(dt.getUTCMinutes()).padStart(2, '0')
        return `${hh}:${mm}`
    }

    const s = t(startIso),
        e = t(endIso)
    return s && e && s !== e ? `${datePart} เวลา ${s}-${e} น.` : s ? `${datePart} เวลา ${s} น.` : datePart
}

export const buildAgendaPayload = (val: AgendaEditorForm) => {
    return {
        agendaId: val.agendaId,
        agendaNo: val.agendaNo ?? '',
        mailId: val.mailId ?? '',
        mailID: val.mailId ?? '',
        agendaName: val.agendaName ?? '',
        meetingUrl: val.meetingUrl ?? '',
        meetingDate: val.meetingDate,
        meetingStartTime: val.meetingStartTime ?? '',
        meetingEndTime: val.meetingEndTime ?? '',
        meetingLocation: val.meetingLocation ?? '',
        agendaDescription: val.agendaDescription ?? '',
        createdAt: val.createdAt,
        submittedStatusId: val.submittedStatusId,
        agendaSummary: val.agendaSummary,
        participants: val.participants,
        topics: serializeAgendaToTopics(val.topics),
    }
}

const mapTopics = (apiTopics: ApiTopic[]) => {
    return (apiTopics ?? []).map((p) => ({
        topicId: p.topicId ?? null,
        title: p.topicName ?? '',
        detail: p.topicDescription ?? '',
        projects: p.projects ?? [],
        topicSummary: p.topicSummary,
        subs: (p.children ?? []).map((c) => ({
            topicId: c.topicId ?? null,
            title: c.topicName ?? '',
            projects: c.projects ?? [],
            topicSummary: c.topicSummary,
        })),
    }))
}

const mapParticipants = (api: ApiAgenda['participants']): ParticipantsFormValues => {
    const mapRow = (r: Participant) => ({
        userId: r.userId,
        user: { value: r.userId },
        email: r?.email ?? '',
        sendMail: !!r?.sendMail,
        agendaParticipantId: r?.agendaParticipantId ?? null,
        fullnameTh: r?.fullnameTh ?? '',
        fullnameEn: r?.fullnameEn ?? '',
    })
    const mapExternal = (r: Participant) => ({
        name: r?.fullnameTh || r?.fullnameEn || '',
        fullnameTh: r?.fullnameTh ?? '',
        fullnameEn: r?.fullnameEn ?? '',
        email: r?.email ?? '',
        sendMail: !!r?.sendMail,
        agendaParticipantId: r?.agendaParticipantId ?? null,
    })

    return {
        attendances: (api?.attendances ?? []).map(mapRow),
        onLeaves: (api?.onLeaves ?? []).map(mapRow),
        presidents: (api?.presidents ?? []).map(mapRow),
        vicePresidents: (api?.vicePresidents ?? []).map(mapRow),
        secretaries: (api?.secretaries ?? []).map(mapRow),
        externalAttendances: (api?.externalAttendances ?? []).map(mapExternal),
    }
}

const isoTimeToHM = (iso?: string | null, stripLeadingZeroHour = true): string => {
    if (!iso) return ''
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    const hh = d.getUTCHours()
    const mm = d.getUTCMinutes()
    const hStr = stripLeadingZeroHour ? String(hh) : String(hh).padStart(2, '0')
    const mStr = String(mm).padStart(2, '0')
    return `${hStr}:${mStr}`
}

export const mapAgendaResponseToForm = (data: ApiAgenda): AgendaEditorForm => {
    return {
        agendaId: data.agendaId,
        agendaNo: data.agendaNo,
        mailId: data.mailId ?? data.mailID ?? '',
        agendaName: data.agendaName,
        meetingUrl: data.meetingUrl,
        meetingDate: data.meetingDate,
        meetingStartTime: isoTimeToHM(data.meetingStartTime),
        meetingEndTime: isoTimeToHM(data.meetingEndTime),
        meetingLocation: data.meetingLocation,
        agendaDescription: data.agendaDescription,
        createdAt: data.createdAt,
        submittedStatusId: data.submittedStatusId,
        participants: mapParticipants(data.participants || []),
        topics: { agendas: mapTopics(data.topics || []) },
        approvalStatusId: null,
        agendaSummary: data.agendaSummary || '',
        remark: data.remark || '',
    }
}

export const formatYmd = (d: string | Date) => {
    const dt = new Date(d || Date.now())
    const y = dt.getFullYear()
    const m = String(dt.getMonth() + 1).padStart(2, '0')
    const day = String(dt.getDate()).padStart(2, '0')
    return `${y}${m}${day}`
}

// แถวที่ไม่มีทั้ง userId และชื่อ คือแถวเปล่า ไม่นับเป็นคน
export const hasRealPerson = (r: any) =>
    r?.userId != null ||
    String(r?.fullnameTh ?? '').trim() !== '' ||
    String(r?.fullnameEn ?? '').trim() !== ''

// บันทึกจริง (ไม่ใช่ร่าง) ต้องมีผู้เข้ามาประชุมอย่างน้อย 1 คน
export const hasAtLeastOneAttendance = (values: any, prefix = 'participants') =>
    ((values?.[prefix]?.attendances ?? []) as any[]).some(hasRealPerson)
